import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import licenseService from '../services/licenseService';

const LicenseContext = createContext();

export const useLicense = () => {
  const context = useContext(LicenseContext);
  if (!context) {
    throw new Error('useLicense must be used within a LicenseProvider');
  }
  return context;
};

export const LicenseProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLicenseWarning, setShowLicenseWarning] = useState(false);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const intervalRef = useRef(null);

  const fetchLicenseInfo = useCallback(async (isManualRefresh = false) => {
    try {
      // Only show loading spinner on initial fetch or manual refresh
      if (!hasFetchedOnce || isManualRefresh) {
        setLoading(true);
      }
      setError(null);
      const response = await licenseService.getLicenseStatus();
      const info = response.data || {};

      // Map the status response to license info
      const newLicenseInfo = {
        status: info.status || 'UNKNOWN',
        isValid: info.valid ?? true,
        isExpired: info.blocked ?? false,
        isInGracePeriod: info.isInGracePeriod ?? false,
        showWarning: info.showWarning ?? false,
        warningMessage: info.warningMessage,
        daysRemaining: info.daysRemaining
      };

      setLicenseInfo(newLicenseInfo);
      setHasFetchedOnce(true);

      // Show warning if license is expiring soon or in grace period
      if (info.showWarning || info.isInGracePeriod) {
        setShowLicenseWarning(true);
      }

      // If license is blocked, stop the refresh interval to prevent spamming
      if (info.blocked) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }

      return newLicenseInfo;
    } catch (err) {
      // Only log error once, not on every interval
      if (!hasFetchedOnce) {
        console.error('Failed to fetch license info:', err);
      }
      setError(err.response?.data?.message || 'Failed to fetch license information');

      // Check if this is a license error from the filter
      if (err.response?.status === 403 && err.response?.data?.error === 'LICENSE_INVALID') {
        const blockedInfo = {
          status: err.response.data.details?.status || err.response.data.status || 'INVALID',
          isValid: false,
          isExpired: true,
          statusMessage: err.response.data.message,
          expirationDate: err.response.data.details?.expirationDate,
          graceExpirationDate: err.response.data.details?.graceExpirationDate
        };
        setLicenseInfo(blockedInfo);
        setHasFetchedOnce(true);

        // Stop interval when license is blocked
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }

        return blockedInfo;
      } else {
        // For other errors (network, server down), assume license is valid to not block the UI
        // but mark that we've attempted fetch
        setLicenseInfo({
          status: 'UNKNOWN',
          isValid: true,
          isExpired: false
        });
        setHasFetchedOnce(true);
      }
    } finally {
      setLoading(false);
    }
  }, [hasFetchedOnce]);

  // Fetch license info when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Initial fetch
      fetchLicenseInfo();

      // Only start refresh interval if not already blocked
      // The interval will be cleared inside fetchLicenseInfo if license becomes blocked
      intervalRef.current = setInterval(() => {
        // Silent refresh - don't show loading state
        fetchLicenseInfo(false);
      }, 5 * 60 * 1000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    } else {
      // Not authenticated - reset license state and stop loading
      setLicenseInfo(null);
      setShowLicenseWarning(false);
      setLoading(false);
      setHasFetchedOnce(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [isAuthenticated, fetchLicenseInfo]);

  const refreshLicense = async () => {
    try {
      setLoading(true);
      setError(null);

      // Try to use the /refresh endpoint first (admin only)
      // Fall back to status check if that fails
      let info;
      try {
        const response = await licenseService.refreshLicense();
        info = response.data?.data || response.data;
      } catch (refreshErr) {
        // If refresh endpoint fails (not admin), fall back to status check
        if (refreshErr.response?.status === 403) {
          const statusResponse = await licenseService.getLicenseStatus();
          info = statusResponse.data || {};
          // Map status response to full info format
          info = {
            status: info.status || 'UNKNOWN',
            isValid: info.valid ?? true,
            isExpired: info.blocked ?? false,
            isInGracePeriod: info.isInGracePeriod ?? false,
            showWarning: info.showWarning ?? false,
            warningMessage: info.warningMessage,
            daysRemaining: info.daysRemaining
          };
        } else {
          throw refreshErr;
        }
      }

      setLicenseInfo(info);

      if (info?.showWarning || info?.isInGracePeriod) {
        setShowLicenseWarning(true);
      } else {
        setShowLicenseWarning(false);
      }

      // If license is now valid and interval was stopped, restart it
      if (info?.isValid && !info?.isExpired && !intervalRef.current) {
        intervalRef.current = setInterval(() => {
          fetchLicenseInfo(false);
        }, 5 * 60 * 1000);
      }

      return info;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to refresh license';
      setError(errorMessage);

      // If we get a license error, update the license info
      if (err.response?.status === 403 && err.response?.data?.error === 'LICENSE_INVALID') {
        setLicenseInfo({
          status: err.response.data.details?.status || err.response.data.status || 'INVALID',
          isValid: false,
          isExpired: true,
          statusMessage: err.response.data.message
        });
      }

      throw err;
    } finally {
      setLoading(false);
    }
  };

  const dismissLicenseWarning = () => {
    setShowLicenseWarning(false);
  };

  // Computed values
  const isLicenseValid = licenseInfo?.isValid ?? true;
  const licenseBlocked = licenseInfo?.isExpired === true ||
                         licenseInfo?.status === 'EXPIRED' ||
                         licenseInfo?.status === 'INVALID_SIGNATURE' ||
                         licenseInfo?.status === 'INVALID_MACHINE' ||
                         licenseInfo?.status === 'REVOKED' ||
                         licenseInfo?.status === 'NOT_FOUND' ||
                         licenseInfo?.status === 'CLOCK_TAMPERED';
  const isInGracePeriod = licenseInfo?.isInGracePeriod ?? false;
  const daysRemaining = licenseInfo?.daysRemaining ?? null;
  const daysInGrace = licenseInfo?.daysInGrace ?? null;
  const warningMessage = licenseInfo?.warningMessage ?? null;

  const value = {
    licenseInfo,
    loading,
    error,
    showLicenseWarning,
    isLicenseValid,
    licenseBlocked,
    isInGracePeriod,
    daysRemaining,
    daysInGrace,
    warningMessage,
    fetchLicenseInfo,
    refreshLicense,
    dismissLicenseWarning
  };

  return (
    <LicenseContext.Provider value={value}>
      {children}
    </LicenseContext.Provider>
  );
};

export default LicenseContext;
